import { useMemo } from 'react';
import { OrgStatus } from '@/domain/shared/shared';
import { PropertyStatus } from '@/domain/entities/property';
import type {
  DashboardActivityItem,
  OrgDashboardSnapshot,
  SuperAdminDashboardSnapshot,
} from '@/features/dashboard/dashboardTypes';
import { useAppSelector } from '@/store/hooks';

function sortByUpdatedAtDesc<T extends { updatedAt?: number }>(items: T[]) {
  return [...items].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
}

function formatRelativeFromEpoch(epoch?: number) {
  if (!epoch) return 'N/A';
  const ms = epoch < 1_000_000_000_000 ? epoch * 1000 : epoch;
  const diffMinutes = Math.floor((Date.now() - ms) / (60 * 1000));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function useDashboardSnapshots() {
  const properties = useAppSelector((state) => state.properties.items);
  const templatesByStatus = useAppSelector((state) => state.propertyTemplates.itemsByStatus);
  const organizations = useAppSelector((state) => state.organizations.items);
  const activeOrganizationId = useAppSelector((state) => state.organizations.activeOrganizationId);
  const membersByOrgId = useAppSelector((state) => state.organizations.membersByOrgId);
  const subscriptions = useAppSelector((state) => state.subscriptions.subscriptions);
  const homeownersCount = useAppSelector((state) => state.homeowners.listIds.length);

  const orgSnapshot = useMemo<OrgDashboardSnapshot>(() => {
    const activeOrg = organizations.find((org) => org.id === activeOrganizationId) ?? null;
    const orgMembersCount = activeOrganizationId ? (membersByOrgId[activeOrganizationId]?.length ?? 0) : 0;
    const orgCapabilitiesCount = activeOrg?.capabilities?.length ?? 0;

    const uniqueTemplateIds = new Set<string>();
    for (const item of templatesByStatus.draft) uniqueTemplateIds.add(item.id);
    for (const item of templatesByStatus.published) uniqueTemplateIds.add(item.id);
    for (const item of templatesByStatus.deprecated) uniqueTemplateIds.add(item.id);

    const handoversInProgressCount = properties.filter((p) => p.status === PropertyStatus.HandoverInitiated).length;
    const readyForHandoverCount = properties.filter((p) => p.status === PropertyStatus.ReadyForHandover).length;

    const recentProperties = sortByUpdatedAtDesc(properties)
      .slice(0, 3)
      .map<DashboardActivityItem>((property) => ({
        id: `property-${property.id}`,
        title: `Property updated (${property.id})`,
        timestampLabel: formatRelativeFromEpoch(property.updatedAt),
        description: property.status ? `Status: ${property.status}` : 'Property metadata changed',
        href: `/properties/${encodeURIComponent(property.id)}/edit`,
      }));

    const recentTemplates = sortByUpdatedAtDesc([
      ...templatesByStatus.draft,
      ...templatesByStatus.published,
      ...templatesByStatus.deprecated,
    ])
      .slice(0, 2)
      .map<DashboardActivityItem>((template) => ({
        id: `template-${template.id}`,
        title: `Template updated: ${template.title || template.id}`,
        timestampLabel: formatRelativeFromEpoch(template.updatedAt),
        description: `Audience: ${template.audience} · Status: ${template.status}`,
        href: `/templates/${encodeURIComponent(template.id)}/edit`,
      }));

    const recentActivity = [...recentProperties, ...recentTemplates].slice(0, 5);

    return {
      generatedAt: Date.now(),
      activeProperties: properties.length,
      templatesCount: uniqueTemplateIds.size,
      orgMembersCount,
      orgCapabilitiesCount,
      handoversInProgressCount,
      recentActivity:
        recentActivity.length > 0
          ? recentActivity
          : [
              {
                id: 'placeholder-activity',
                title: 'No recent activity loaded yet',
                timestampLabel: 'Pending snapshot API',
                description: 'Dashboard snapshot endpoint can populate activity feed.',
              },
            ],
      attentionItems: [
        {
          id: 'ready-for-handover',
          label: `${readyForHandoverCount} properties ready for handover`,
          tone: readyForHandoverCount > 0 ? 'warning' : 'info',
          href: '/properties',
        },
        {
          id: 'in-progress-handover',
          label: `${handoversInProgressCount} handovers currently in progress`,
          tone: handoversInProgressCount > 0 ? 'warning' : 'info',
          href: '/properties',
        },
        {
          id: 'capability-review',
          label: `${orgCapabilitiesCount} org capabilities currently enabled`,
          tone: 'info',
          href: activeOrg ? `/admin/organizations/${encodeURIComponent(activeOrg.id)}` : '/admin/organizations',
        },
      ],
    };
  }, [activeOrganizationId, membersByOrgId, organizations, properties, templatesByStatus]);

  const superAdminSnapshot = useMemo<SuperAdminDashboardSnapshot>(() => {
    const activeOrganizationsCount = organizations.filter((org) => org.status === OrgStatus.Active).length;
    const activeSubscriptionsCount = subscriptions.filter((sub) => sub.status === 'active').length;
    const pastDueSubscriptionsCount = subscriptions.filter((sub) => sub.status === 'past_due').length;

    const subscriptionTierBreakdown = [
      { tier: 'free' as const, count: subscriptions.filter((s) => s.tier === 'free').length },
      { tier: 'silver' as const, count: subscriptions.filter((s) => s.tier === 'silver').length },
      { tier: 'gold' as const, count: subscriptions.filter((s) => s.tier === 'gold').length },
      { tier: 'platinum' as const, count: subscriptions.filter((s) => s.tier === 'platinum').length },
    ];

    const subscriptionStatusBreakdown = [
      { status: 'active' as const, count: subscriptions.filter((s) => s.status === 'active').length },
      { status: 'canceled' as const, count: subscriptions.filter((s) => s.status === 'canceled').length },
      { status: 'expired' as const, count: subscriptions.filter((s) => s.status === 'expired').length },
      { status: 'past_due' as const, count: subscriptions.filter((s) => s.status === 'past_due').length },
    ];

    return {
      generatedAt: Date.now(),
      organizationsCount: organizations.length,
      propertiesCount: properties.length,
      homeOwnersCount: homeownersCount,
      activeSubscriptionsCount,
      pastDueSubscriptionsCount,
      subscriptionTierBreakdown,
      subscriptionStatusBreakdown,
      recentProjects: sortByUpdatedAtDesc(properties)
        .slice(0, 6)
        .map((property) => ({
          id: property.id,
          orgID: property.orgID,
          status: property.status ?? 'unknown',
          updatedAt: property.updatedAt,
        })),
      operationalAlerts: [
        {
          id: 'alert-past-due-subs',
          label: `${pastDueSubscriptionsCount} subscriptions are past due`,
          tone: pastDueSubscriptionsCount > 0 ? 'critical' : 'info',
          href: '/admin/subscriptions',
        },
        {
          id: 'alert-org-coverage',
          label: `${Math.max(organizations.length - activeOrganizationsCount, 0)} organizations not active`,
          tone: organizations.length > activeOrganizationsCount ? 'warning' : 'info',
          href: '/admin/organizations',
        },
        {
          id: 'alert-snapshot-placeholder',
          label: 'Connect super-admin snapshot endpoint for system-wide KPIs',
          tone: 'info',
        },
      ],
    };
  }, [homeownersCount, organizations, properties, subscriptions]);

  return { orgSnapshot, superAdminSnapshot };
}
